import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { Observation } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitCancelButtons } from "./SubmitCancelButtons";
import type {
  GenericObservationDisplayProps,
  GenericObservationFormProps,
} from "./types";

const formSchema = z.object({
  value: z.string(),
});

const observationPresenceOptionsMapping = {
  [Observation.DataAbsentReason.values.ASKED_UNKNOWN]: "Not Indicated",
  [Observation.DataAbsentReason.values.TEMP_UNKNOWN]: "Completed",
  [Observation.DataAbsentReason.values.ASKED_DECLINED]: "Declined",
};

export const GenericObservationPresenceForm = ({
  patientId,
  item,
  itemCategory,
  onSubmit,
  onCancel,
  isSubmitting,
}: GenericObservationFormProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      value: item.documentedObservation?.dataAbsentReason?.coding?.[0]?.code,
    },
  });
  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    const payload = {
      patientId: patientId as string,
      category: itemCategory,
      status: "final",
      code: {
        coding: [
          {
            code: item.observationCode,
            system: item.observationCodeSystem,
            display: item.observationCodeDisplay,
          },
        ],
        text: item.observationCodeFriendlyDisplay,
      },
      dataAbsentReason: data.value,
    };
    onSubmit(payload);
  };
  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2">
        <Controller
          control={form.control}
          name="value"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="value">
                <BodyText size="sm" bold>
                  {item.observationCodeFriendlyDisplay}
                </BodyText>
              </FieldLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="value" aria-invalid={fieldState.invalid}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(observationPresenceOptionsMapping).map(
                    ([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </div>
    </form>
  );
};

export const GenericObservationPresenceDisplay = ({
  item,
}: GenericObservationDisplayProps) => {
  let displayValue = "Unknown";
  if (item.documentedObservation?.dataAbsentReason) {
    displayValue =
      observationPresenceOptionsMapping[
        item.documentedObservation?.dataAbsentReason?.coding?.[0]
          ?.code as keyof typeof observationPresenceOptionsMapping
      ] || "Unknown";
  }
  return <BodyText size="sm">{displayValue}</BodyText>;
};
